import type { TFunction } from "i18next"

import type { Summary, Unit } from "../types/automation.types"
import { type SummaryInput, UNIT_SECONDS } from "./summary"

const PARTS: Unit[] = ["days", "hours", "minutes"]

// German takes the dative after "nach" / "im Abstand von" ("nach 2 Tagen"), so
// each duration is offered in both forms and the locale's template picks one.
const durationText = (
  t: TFunction,
  value: number,
  unit: Unit,
  dative: boolean
): string => {
  let remaining = value * UNIT_SECONDS[unit]
  const parts: string[] = []
  for (const part of PARTS) {
    const count = Math.floor(remaining / UNIT_SECONDS[part])
    remaining -= count * UNIT_SECONDS[part]
    if (count) {
      parts.push(
        t(`settingsAutomation.duration.${part}`, {
          count,
          context: dative ? "dative" : undefined
        })
      )
    }
  }
  return parts.length
    ? parts.join(" ")
    : t("settingsAutomation.duration.minutes", { count: 0 })
}

/** The shared helper's compound duration, in the current language. */
export const localizeDuration = (t: TFunction, value: number, unit: Unit) =>
  durationText(t, value, unit, false)

/** Both grammatical forms, as interpolation values named `<name>` and `<name>Dative`. */
export const durationVars = (
  t: TFunction,
  name: string,
  value: number,
  unit: Unit
) => ({
  [name]: durationText(t, value, unit, false),
  [`${name}Dative`]: durationText(t, value, unit, true)
})

// A repeat still being filled in (Total sends or the interval cleared) reads
// without them rather than as "Send  times" or "every 0 minutes".
const repeatVariant = (countSet: boolean, cadenceSet: boolean) =>
  countSet ? (cadenceSet ? "" : "NoCadence") : cadenceSet ? "NoCount" : "Unset"

const isSetCadence = (
  value: number | null,
  unit: Unit | null
): value is number =>
  unit != null &&
  typeof value === "number" &&
  Number.isFinite(value) &&
  value > 0

// The shared `describe` rule in the current language. `describe` itself stays
// English and byte-identical to the backend; in English this matches it
// exactly (pinned against the backend vectors in the tests).
export const localizeSummary = (t: TFunction, input: SummaryInput): Summary => {
  const delay = durationVars(t, "delay", input.delay_value, input.delay_unit)
  const repeating = input.send_count !== 1
  const cadenceSet = isSetCadence(input.cadence_value, input.cadence_unit)
  const cadence = cadenceSet
    ? durationVars(
        t,
        "cadence",
        input.cadence_value as number,
        input.cadence_unit as Unit
      )
    : {}
  const variant = repeatVariant(
    Number.isInteger(input.send_count) && input.send_count > 1,
    cadenceSet
  )
  const repeatVars = { count: input.send_count, ...cadence }
  const nurturing = input.category === "NURTURING"
  const opening = nurturing
    ? t("settingsAutomation.summary.nurturingSentence", delay)
    : t("settingsAutomation.summary.sentence", {
        state: input.state_label,
        sender: input.sender_email,
        ...delay
      })
  // sendRepeat and repeatSends have no singular form: they only ever see a count of 2 or more.
  const closing = repeating
    ? t(`settingsAutomation.summary.sendRepeat${variant}`, repeatVars)
    : t("settingsAutomation.summary.sendOnce")

  return {
    sentence: `${opening} ${closing}`,
    steps: [
      {
        key: "trigger",
        title: t("settingsAutomation.summary.triggerTitle"),
        text: nurturing
          ? t("settingsAutomation.summary.nurturingTriggerText")
          : t("settingsAutomation.summary.triggerText", {
              state: input.state_label
            })
      },
      {
        key: "wait",
        title: t("settingsAutomation.summary.waitTitle"),
        text: t(
          `settingsAutomation.summary.${nurturing ? "nurturingWaitText" : "waitText"}`,
          delay
        )
      },
      {
        key: "send",
        title: t("settingsAutomation.summary.sendTitle"),
        text: repeating
          ? t(`settingsAutomation.summary.repeatSends${variant}`, repeatVars)
          : t("settingsAutomation.summary.oneSend")
      }
    ]
  }
}
